import { createAsyncThunk } from "@reduxjs/toolkit";
import { Immutable } from "immer";

import * as basketAPI from "../../../api/basket";
// Import the concrete module, not the `apps/checkout/thunks` barrel —
// that barrel re-exports this file, so it would create a cycle.
import { loadBasket } from "../../../apps/checkout/thunks/loadBasket";
import { IBasket } from "../../../models/catalogue";
import { IBasketLineAPIURL } from "../../../models/nominals";
import { HttpError } from "../../../utils/ajax";

/**
 * Arguments for updateBasketLineQuantity thunk.
 */
interface UpdateBasketLineQuantityArgs {
    lineURL: IBasketLineAPIURL;
    newQty: number;
}

/**
 * Rejection payload when the server refuses the new quantity because it
 * exceeds the available stock (HTTP 406 from the basket line endpoint).
 *
 * `dispatch(updateBasketLineQuantity(...)).unwrap()` throws this payload, so
 * callers use `isBasketLineStockError` to tell it apart from other failures.
 */
export type IBasketLineStockError = Immutable<{
    isStockError: true;
}>;

export const isBasketLineStockError = (
    err: unknown,
): err is IBasketLineStockError =>
    typeof err === "object" &&
    err !== null &&
    "isStockError" in err &&
    err.isStockError === true;

/**
 * Update the quantity of a basket line.
 *
 * This thunk updates the quantity of an existing product in the basket
 * and reloads the basket to reflect pricing and offer changes. Used
 * primarily in the cart view for quantity adjustments.
 *
 * When the requested quantity exceeds available stock the server answers
 * 406 and the thunk rejects with an `IBasketLineStockError`; the basket is
 * not reloaded because the line was left unchanged (#34678). Other errors
 * propagate as-is.
 *
 * @param {IBasketLineAPIURL} args.lineURL - API URL of the basket line to update
 * @param {number} args.newQty - New quantity for the line
 *
 * @example
 * // Update line quantity to 3
 * dispatch(updateBasketLineQuantity({
 *   lineURL: line.url,
 *   newQty: 3
 * }));
 *
 * @sideEffects
 * - Makes API call to update line quantity via basketAPI.updateLineQuantity
 * - Dispatches loadBasket thunk to reload basket data
 * - Updates `data.basket` with reloaded basket via extraReducers
 * - May trigger offer recalculations on the backend
 */
// Prefix kept as "checkout/": module only moved, and nothing matches these
// types by string, so renaming would change them unnoticed.
export const updateBasketLineQuantity = createAsyncThunk<
    IBasket | null,
    UpdateBasketLineQuantityArgs,
    { rejectValue: IBasketLineStockError }
>(
    "checkout/updateBasketLineQuantity",
    async (args, { dispatch, rejectWithValue }) => {
        try {
            await basketAPI.updateLineQuantity(args.lineURL, args.newQty);
        } catch (err) {
            if (err instanceof HttpError && err.status === 406) {
                return rejectWithValue({ isStockError: true });
            }
            throw err;
        }
        const result = await dispatch(loadBasket()).unwrap();
        return result.basket;
    },
);
